import type { Membresia, Operaciones, ParDeTokens, RetoDosPasos, Yo } from '../api/operaciones';
import { esRetoDosPasos } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { RUTAS_ACCESO } from '../rutas';

/**
 * Lógica del inicio de sesión (1.3) y de elegir negocio (1.4), sin React ni estilos, para probarla sola y que la use la
 * pantalla de acceso que se construya con la exportación de Claude Code (G127, ADR-0050). Textos de los mensajes: los
 * del diseño y los aprobados por el equipo (G122, G123). Las reglas de verdad (contraseña filtrada,
 * intentos, caducidad del código) las aplica la API; aquí solo se evita enviar lo que seguro está mal.
 */
/** Pantallas del acceso (1.3, 1.4) y su ruta. */
export type PantallaAcceso = 'login' | 'twofa' | 'biz' | 'force2fa' | 'forgot' | 'forgotSent' | 'reset' | 'resetDone' | 'verify' | 'signup' | 'offer2fa' | 'newbiz';

export const RUTA_DE_PANTALLA: Record<PantallaAcceso, string> = {
    login: RUTAS_ACCESO.entrar,
    twofa: RUTAS_ACCESO.dosPasos,
    biz: RUTAS_ACCESO.elegirNegocio,
    force2fa: RUTAS_ACCESO.activarDosPasos,
    forgot: RUTAS_ACCESO.olvidada,
    forgotSent: RUTAS_ACCESO.enviada,
    reset: RUTAS_ACCESO.nueva,
    resetDone: RUTAS_ACCESO.cambiada,
    verify: RUTAS_ACCESO.verificarEmail,
    signup: RUTAS_ACCESO.crearCuenta,
    offer2fa: RUTAS_ACCESO.protegerCuenta,
    newbiz: RUTAS_ACCESO.crearNegocio,
};

/** Pantalla del acceso que corresponde a una ruta (la de entrar si no es ninguna). */
export function pantallaDeRuta(ruta: string): PantallaAcceso {
    const encontrada = (Object.entries(RUTA_DE_PANTALLA) as [PantallaAcceso, string][]).find(([, r]) => r === ruta);
    return encontrada?.[0] ?? 'login';
}

export const MENSAJES = {
    emailNoValido: 'Escribe un email válido.',
    faltaContrasena: 'Escribe tu contraseña.',
    noCoinciden: 'El email o la contraseña no coinciden. Prueba de nuevo.',
    faltanNumeros: 'Escribe los 6 números.',
    recuperacionCorta: 'El código de recuperación tiene 8 caracteres.',
    codigoIncorrecto: 'El código no es correcto o ha caducado. Prueba con el que aparece ahora en tu aplicación.',
    minimo12: 'Mínimo 12 caracteres.',
    noCoincidenNuevas: 'Las dos contraseñas no coinciden.',
    // Estados que el diseño no dibuja: textos aprobados por el equipo (G122, ADR-0047; tabla de P0-11), tal cual.
    retoCaducado: 'Ha pasado demasiado tiempo. Vuelve a escribir tu email y tu contraseña.',
    sinConexion: 'No he podido conectar con Reservaria. Prueba de nuevo.',
    bloqueada: 'Hemos parado la entrada un rato por demasiados intentos. Prueba de nuevo más tarde.',
    emailSinVerificar: 'Tu email aún no está verificado. Abre el enlace que te enviamos al correo y vuelve a entrar.',
    sinNegocio: 'Todavía no trabajas en ningún negocio. Pide a quien lo lleve que te invite.',
    otraCuenta: 'Esta cuenta no es de un equipo de negocio: entra desde el portal de partners o el backoffice.',
    // Textos aprobados para los estados de entrada que faltaban (G123, ADR-0047; tabla de P0-13), tal cual.
    soporteSinDosPasos: 'Tu cuenta de Reservaria necesita la verificación en dos pasos. Actívala para continuar.',
    /** Título de la pantalla obligatoria (`force2fa`, G123). */
    activaDosPasosTitulo: 'Activa la verificación en dos pasos',
    soporteConProveedor: 'Las cuentas del equipo de Reservaria entran con email, contraseña y código. Usa esa forma de entrar.',
    invitacionOtroEmail: 'Esta invitación es para otro email. Entra con la cuenta de ese email.',
    codigoEnElMomento: 'Para hacer esto, escribe el código de tu aplicación de verificación.',
    // G126: 429 sin la cabecera Retry-After.
    demasiadosIntentos: 'Demasiados intentos. Prueba de nuevo en unos minutos.',
} as const;

/** G123: unir una cuenta que ya existe con Google o Apple (`link_required`). */
export function textoUneTuCuenta(proveedor: 'Google' | 'Apple'): string {
    return `Ya tienes una cuenta con este email. Escribe tu contraseña una vez para unirla con ${proveedor}.`;
}

/** G123: vuelta del proveedor cancelada o fallida (`?error=` en la vuelta). */
export function textoVueltaFallida(proveedor: 'Google' | 'Apple'): string {
    return `No se ha completado la entrada con ${proveedor}. Prueba de nuevo o entra con tu email.`;
}

/**
 * ¿Es una cuenta de Soporte o Superadmin sin los dos pasos activados (G65)? La API responde `403 two_factor_required` al
 * entrar en un negocio o en el backoffice; el panel lleva a «Activa la verificación en dos pasos» (G123).
 */
export function faltanDosPasos(error: unknown): boolean {
    return error instanceof ErrorApi && error.codigo === 'two_factor_required';
}

/** ¿Falta verificar el email de la cuenta (G66)? La API responde `403 email_not_verified` al entrar en un negocio. */
export function faltaVerificarEmail(error: unknown): boolean {
    return error instanceof ErrorApi && error.codigo === 'email_not_verified';
}

/**
 * Textos de 1.2 (verificar el email), tal cual del diseño. En el alta de un propietario nuevo (1.1, tarea 490) llevan
 * «Paso 2 de 4».
 */
export const TEXTOS_VERIFICAR = {
    comprobando: 'Comprobando el enlace…',
    revisa: 'Revisa tu correo',
    revisaSub: (email: string) => `Te hemos enviado un enlace a ${email}. Ábrelo para seguir: sirve durante 24 horas.`,
    noHaLlegado: '¿No te ha llegado?',
    reenviar: 'Reenviar el enlace',
    otroEmail: '¿Es otro email?',
    cambiarlo: 'Cambiarlo',
    corrige: 'Corrige tu email',
    corrigeSub: 'Te enviamos el enlace al nuevo; el anterior deja de servir.',
    enviarEnlace: 'Enviar el enlace',
    verificado: 'Email verificado',
    verificadoSub: 'Ahora, protégela y crea tu negocio.',
    caducado: 'Este enlace ha caducado',
    caducadoSub: 'Por seguridad, los enlaces caducan a las 24 horas.',
    caducadoNuevo: (email: string) => `Te enviamos uno nuevo a ${email}.`,
    enlaceNuevo: 'Enviar un enlace nuevo',
} as const;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailValido(valor: string): boolean {
    return EMAIL.test(valor.trim());
}

export interface ErroresEntrada {
    email?: string;
    password?: string;
}

export function validarEntrada(email: string, password: string): ErroresEntrada {
    const errores: ErroresEntrada = {};
    if (!emailValido(email)) {
        errores.email = MENSAJES.emailNoValido;
    }
    if (password === '') {
        errores.password = MENSAJES.faltaContrasena;
    }
    return errores;
}

/** Error del segundo paso antes de enviarlo, o null si se puede enviar. */
export function validarSegundoPaso(recuperacion: boolean, codigo: string): string | null {
    if (recuperacion) {
        return codigo.replace(/\W/g, '').length < 8 ? MENSAJES.recuperacionCorta : null;
    }
    return /^\d{6}$/.test(codigo) ? null : MENSAJES.faltanNumeros;
}

export interface ErroresNueva {
    nueva?: string;
    repetida?: string;
}

export function validarNuevaContrasena(nueva: string, repetida: string): ErroresNueva {
    const errores: ErroresNueva = {};
    if (nueva.length < 12) {
        errores.nueva = MENSAJES.minimo12;
    }
    if (repetida !== nueva) {
        errores.repetida = MENSAJES.noCoincidenNuevas;
    }
    return errores;
}

/** Fuerza de la contraseña para la barra de tres tramos: 0 vacía, 1 corta, 2 vale, 3 fuerte. */
export function fuerzaContrasena(p: string): 0 | 1 | 2 | 3 {
    if (p === '') {
        return 0;
    }
    if (p.length < 12) {
        return 1;
    }
    const tipos = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(p)).length;
    return p.length >= 16 || tipos >= 3 ? 3 : 2;
}

export function textoFuerza(p: string): string {
    const fuerza = fuerzaContrasena(p);
    return [
        'Mínimo 12 caracteres. Comprobamos que no aparezca en filtraciones.',
        `Te faltan ${String(12 - p.length)} caracteres.`,
        'Vale. Mezcla mayúsculas, números o símbolos para hacerla más fuerte.',
        'Fuerte.',
    ][fuerza] as string;
}

/** Texto para el recuadro rojo del formulario a partir del error de la API (por su código estable). */
export function mensajeDeError(error: unknown, paso: 'entrar' | 'dos-pasos' | 'contrasena' | 'negocio'): string {
    if (!(error instanceof ErrorApi)) {
        return MENSAJES.sinConexion;
    }
    switch (error.codigo) {
        case 'unauthenticated':
            return paso === 'entrar' ? MENSAJES.noCoinciden : paso === 'dos-pasos' ? MENSAJES.retoCaducado : error.problema.detail ?? error.problema.title;
        case 'code_invalid':
            return MENSAJES.codigoIncorrecto;
        case 'email_not_verified':
            return MENSAJES.emailSinVerificar;
        case 'conflict':
            return paso === 'entrar' ? MENSAJES.bloqueada : error.problema.detail ?? error.problema.title;
        case 'rate_limited':
            return error.reintentarEn !== null
                ? `Demasiados intentos. Prueba de nuevo dentro de ${espera(error.reintentarEn)}.`
                : MENSAJES.demasiadosIntentos;
        case 'two_factor_required':
            return MENSAJES.soporteSinDosPasos;
        default:
            return error.problema.detail ?? error.problema.title;
    }
}

/** Mensaje de la API para un campo concreto (`errors[].field`), si lo trae. */
export function errorDeCampo(error: unknown, campo: string): string | null {
    if (!(error instanceof ErrorApi)) {
        return null;
    }
    const delCampo = error.errores.find((e) => e.field === campo);
    return delCampo === undefined ? null : (delCampo.message ?? MENSAJES.minimo12);
}

export type ResultadoEntrada = { tipo: 'sesion'; tokens: ParDeTokens } | { tipo: 'dos-pasos'; reto: RetoDosPasos };

/** createSession: o la sesión, o el reto de dos pasos. */
export async function entrar(ops: Operaciones, email: string, password: string): Promise<ResultadoEntrada> {
    const respuesta = await ops.createSession({ email: email.trim(), password });
    return esRetoDosPasos(respuesta) ? { tipo: 'dos-pasos', reto: respuesta } : { tipo: 'sesion', tokens: respuesta };
}

/** completeTwoFactorChallenge con el código de la aplicación o con un código de recuperación. */
export function completarDosPasos(ops: Operaciones, reto: RetoDosPasos, recuperacion: boolean, codigo: string): Promise<ParDeTokens> {
    return ops.completeTwoFactorChallenge(
        recuperacion ? { challenge_token: reto.challenge_token, recovery_code: codigo.trim() } : { challenge_token: reto.challenge_token, code: codigo },
    );
}

/**
 * Tras entrar sin negocio fijado (1.4): con un solo negocio se entra directamente; con varios, se elige (sin negocio
 * por defecto); sin ninguno, no hay panel que abrir.
 */
export type NegocioAlEntrar =
    | { tipo: 'directo'; negocio: Membresia }
    | { tipo: 'elegir'; negocios: Membresia[] }
    | { tipo: 'ninguno' }
    /** Cuenta de partner o de plataforma: su sitio es el portal o el backoffice, no este panel (P0-11). */
    | { tipo: 'otra-cuenta' };

export function negocioAlEntrar(yo: Yo): NegocioAlEntrar {
    if (yo.account.type !== 'staff') {
        return { tipo: 'otra-cuenta' };
    }
    const [primero] = yo.memberships;
    if (primero === undefined) {
        return { tipo: 'ninguno' };
    }
    return yo.memberships.length === 1 ? { tipo: 'directo', negocio: primero } : { tipo: 'elegir', negocios: yo.memberships };
}

/**
 * Varios nombres con «y» antes del último (G995): «Propietario y Encargado»; con tres, «Propietario, Encargado y
 * Recepción»; uno solo, tal cual; ninguno, vacío.
 */
export function conY(nombres: readonly string[]): string {
    return nombres.length <= 1 ? (nombres[0] ?? '') : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1] ?? ''}`;
}

/**
 * Los roles de la persona en ese negocio con «y» antes del último (G995): el propietario lleva «Propietario» delante
 * («Propietario y Encargado»), como en Equipo (`rolesDeMiembro`), porque la propiedad no es un rol de `roles`.
 */
export function rolesDe(membresia: Membresia): string {
    const nombres = [...(membresia.is_owner ? ['Propietario'] : []), ...membresia.roles.map((r) => r.name).filter((n) => !(membresia.is_owner && n === 'Propietario'))];
    return conY(nombres);
}

/** «Hola, Uri»: el nombre de pila de la cuenta. */
export function saludo(yo: Yo): string {
    const [nombre = ''] = yo.account.display_name.trim().split(/\s+/);
    return nombre === '' ? 'Hola' : `Hola, ${nombre}`;
}

/** La espera de un `429` (`Retry-After`) en palabras: segundos si es menos de un minuto; si no, minutos redondeados hacia arriba. */
export function espera(segundos: number): string {
    if (segundos < 60) {
        return segundos === 1 ? '1 segundo' : `${String(segundos)} segundos`;
    }
    const minutos = Math.ceil(segundos / 60);
    return minutos === 1 ? '1 minuto' : `${String(minutos)} minutos`;
}
